import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import { useBreakpoint } from '../hooks/useBreakpoint';
import api from '../api/axios';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.a1looks.com';

const CATEGORIES = [
  { name: 'All', slug: '' },
  { name: 'Punjabi Juttis', slug: 'juttis' },
  { name: 'Earrings', slug: 'earrings' },
  { name: 'Necklaces', slug: 'necklaces' },
  { name: 'Bracelets', slug: 'bracelets' },
  { name: 'Rings', slug: 'rings' },
  { name: 'Potli Bags', slug: 'potli-bags' },
];

const CATEGORY_LABEL = {
  juttis: 'Punjabi Juttis',
  earrings: 'Earrings',
  necklaces: 'Necklaces',
  bracelets: 'Bracelets',
  rings: 'Rings',
  'potli-bags': 'Potli Bags',
  'wedding-collection': 'Wedding Collection',
};

const C = { primary: '#8B1A1A', gold: '#D4A017' };

function buildParams(filters, pageNum) {
  const p = new URLSearchParams();
  if (filters.activeCategory) p.set('category', filters.activeCategory);
  if (filters.isBestSeller) p.set('is_best_seller', 'true');
  if (filters.isNewArrival) p.set('is_new_arrival', 'true');
  if (filters.search) p.set('search', filters.search);
  if (filters.sort) p.set('ordering', filters.sort);
  if (filters.maxPrice < 5000) p.set('max_price', String(filters.maxPrice));
  p.set('page', String(pageNum));
  return p;
}

function FilterPanel({ activeCategory, setCategory, maxPrice, setMaxPrice }) {
  const filterHeading = { fontFamily: "'Playfair Display', serif", fontSize: 16, color: '#2C2C2C', marginBottom: 16 };
  return (
    <>
      <h3 style={filterHeading}>Categories</h3>
      {CATEGORIES.map(cat => (
        <label key={cat.slug} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 14, color: activeCategory === cat.slug ? C.primary : '#2C2C2C', fontWeight: activeCategory === cat.slug ? 700 : 400 }}>
          <input type="radio" name="category" checked={activeCategory === cat.slug} onChange={() => setCategory(cat.slug)} style={{ accentColor: C.primary }} />
          {cat.name}
        </label>
      ))}
      <h3 style={{ ...filterHeading, marginTop: 24 }}>Max Price</h3>
      <input type="range" min="100" max="5000" step="100" value={maxPrice} onChange={e => setMaxPrice(Number(e.target.value))} style={{ width: '100%', accentColor: C.primary }} />
      <p style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Up to ₹{maxPrice.toLocaleString('en-IN')}</p>
    </>
  );
}

const CATEGORY_SEO = {
  juttis: {
    title: 'Handcrafted Punjabi Jutti for Women Online | A1 Looks',
    description: 'Buy authentic handcrafted Punjabi Jutti for women online. Traditional leather juttis in sizes 36–42. Ethnic footwear for weddings and festivals. Free shipping above ₹999.',
  },
  earrings: {
    title: 'Oxidized Silver Jhumka Earrings & Kundan Earrings Online | A1 Looks',
    description: 'Shop oxidized silver jhumka earrings, Kundan studs and ethnic earrings under ₹500. Handcrafted for weddings, festivals and daily wear. Free shipping above ₹999.',
  },
  bracelets: {
    title: 'Kashmiri Beaded Bangle Set & Ethnic Bracelets for Weddings | A1 Looks',
    description: 'Buy Kashmiri beaded bangle sets, oxidized bracelets and ethnic bangles for weddings and festivals. Handcrafted and shipped across India.',
  },
  necklaces: {
    title: 'Kundan Necklace Set for Weddings & Festivals Online | A1 Looks',
    description: 'Shop layered Kundan necklace sets and ethnic jewellery for weddings, parties and daily wear. Handcrafted by Indian artisans. Free shipping above ₹999.',
  },
  rings: {
    title: 'Kundan & Crystal Ethnic Rings for Women Online | A1 Looks',
    description: 'Buy Kundan, crystal and oxidized ethnic rings online. Traditional Indian finger rings for weddings, festivals and everyday wear.',
  },
  'potli-bags': {
    title: 'Ethnic Potli Bags & Wedding Clutches Online | A1 Looks',
    description: 'Shop handcrafted ethnic potli bags and clutches for weddings, parties and festivals. Embroidered and beaded designs. Free shipping above ₹999.',
  },
  'wedding-collection': {
    title: 'Wedding Ethnic Jewellery & Jutti Collection Online | A1 Looks',
    description: 'Complete bridal and wedding ethnic collection — Punjabi juttis, Kundan jewellery, potli bags and accessories. Shop the full wedding look at A1 Looks.',
  },
};

function getPageSEO(activeCategory, isBestSeller, isNewArrival, urlMaxPrice) {
  if (isBestSeller) return {
    title: 'Best Selling Handcrafted Punjabi Juttis & Ethnic Jewellery | A1 Looks',
    description: 'Shop the most loved handcrafted Punjabi Juttis, Kundan jewellery, jhumka earrings and ethnic accessories. Top-rated pieces by our customers.',
    canonical: `${SITE_URL}/shop?is_best_seller=true`,
  };
  if (isNewArrival) return {
    title: 'New Ethnic Jewellery & Jutti Arrivals | Latest Designs | A1 Looks',
    description: 'Discover the latest handcrafted Punjabi Juttis, Kundan jewellery and ethnic accessories just landed at A1 Looks. Fresh designs for weddings and festivals.',
    canonical: `${SITE_URL}/shop?is_new_arrival=true`,
  };
  if (urlMaxPrice === '499') return {
    title: 'Ethnic Jewellery Under ₹500 | Jhumka Earrings, Bangles & More | A1 Looks',
    description: 'Affordable handcrafted ethnic jewellery under ₹500 — oxidized jhumka earrings, beaded bangles, Kundan rings and more. Free shipping above ₹999.',
    canonical: `${SITE_URL}/shop?max_price=499`,
  };
  if (urlMaxPrice === '999') return {
    title: 'Handcrafted Ethnic Fashion Under ₹999 | Jutti, Jewellery & Accessories | A1 Looks',
    description: 'Stylish handcrafted ethnic jewellery, Punjabi juttis and accessories under ₹999 at A1 Looks. Great value for weddings and festivals.',
    canonical: `${SITE_URL}/shop?max_price=999`,
  };
  if (activeCategory) {
    const catSeo = CATEGORY_SEO[activeCategory];
    if (catSeo) return { ...catSeo, canonical: `${SITE_URL}/shop?category=${activeCategory}` };
    const label = CATEGORY_LABEL[activeCategory] || activeCategory;
    return {
      title: `${label} | Ethnic Fashion Online | A1 Looks`,
      description: `Shop ${label} — authentic handcrafted ethnic fashion at A1 Looks. Free shipping above ₹999.`,
      canonical: `${SITE_URL}/shop?category=${activeCategory}`,
    };
  }
  return {
    title: 'Shop Handcrafted Punjabi Juttis & Ethnic Jewellery Online | A1 Looks',
    description: 'Browse our complete collection of handcrafted Punjabi Juttis, Kundan jewellery, oxidized earrings, necklaces and ethnic accessories. Free shipping above ₹999. COD available.',
    canonical: `${SITE_URL}/shop`,
  };
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sentinelRef = useRef(null);
  const { isMobile } = useBreakpoint();

  const activeCategory = searchParams.get('category') || '';
  const isBestSeller = searchParams.get('is_best_seller') === 'true';
  const isNewArrival = searchParams.get('is_new_arrival') === 'true';
  const urlMaxPrice = searchParams.get('max_price') || '';
  const [maxPrice, setMaxPrice] = useState(() => urlMaxPrice ? parseInt(urlMaxPrice) : 5000);

  useEffect(() => {
    if (urlMaxPrice) setMaxPrice(parseInt(urlMaxPrice));
    else setMaxPrice(5000);
  }, [urlMaxPrice]);

  // Keep latest filters in a ref so the loadMore effect never goes stale
  const filtersRef = useRef({});
  filtersRef.current = { activeCategory, isBestSeller, isNewArrival, search, sort, maxPrice };

  // Filter change → reset list and load page 1
  useEffect(() => {
    setLoading(true);
    setProducts([]);
    setPage(1);
    setHasMore(false);

    const params = buildParams(filtersRef.current, 1);
    let cancelled = false;

    api.get(`/products/?${params.toString()}`)
      .then(res => {
        if (cancelled) return;
        const data = res.data.results || res.data;
        setProducts(data);
        setTotalCount(res.data.count || data.length);
        setHasMore(!!res.data.next);
      })
      .catch(() => {
        if (!cancelled) { setProducts([]); setTotalCount(0); setHasMore(false); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, isBestSeller, isNewArrival, search, sort, maxPrice]);

  // Page increment (from sentinel) → append next batch
  useEffect(() => {
    if (page === 1) return;
    setLoadingMore(true);

    const params = buildParams(filtersRef.current, page);

    api.get(`/products/?${params.toString()}`)
      .then(res => {
        const data = res.data.results || res.data;
        setProducts(prev => [...prev, ...data]);
        setHasMore(!!res.data.next);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // IntersectionObserver watches the sentinel div; fires page increment when visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setPage(prev => prev + 1); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading]);

  const setCategory = (slug) => {
    const p = new URLSearchParams();
    if (slug) p.set('category', slug);
    setSearchParams(p);
  };

  const categoryLabel = CATEGORY_LABEL[activeCategory];
  const pageTitle = isBestSeller ? 'Best Sellers'
    : isNewArrival ? 'New Arrivals'
    : urlMaxPrice === '499' ? 'Under ₹499'
    : urlMaxPrice === '999' ? 'Under ₹999'
    : activeCategory ? (categoryLabel || activeCategory)
    : 'Shop All';

  const seo = getPageSEO(activeCategory, isBestSeller, isNewArrival, urlMaxPrice);

  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: isMobile ? '24px 16px' : '32px 24px' }}>
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} />

      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 32, color: '#2C2C2C', marginBottom: 4 }}>
        {pageTitle}
      </h1>
      <p style={{ color: '#888', marginBottom: isMobile ? 16 : 28, fontSize: 14 }}>{totalCount} product{totalCount !== 1 ? 's' : ''}</p>

      {/* Mobile filter toggle */}
      {isMobile && (
        <button
          onClick={() => setFiltersOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 16px', background: '#fff', border: `1.5px solid ${filtersOpen ? C.primary : '#e0d0d0'}`, borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: filtersOpen ? C.primary : '#2C2C2C' }}
        >
          Filters {activeCategory || maxPrice < 5000 ? '·' : ''} {filtersOpen ? '▲' : '▼'}
        </button>
      )}

      {/* Mobile collapsible filters */}
      {isMobile && filtersOpen && (
        <div style={{ background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20 }}>
          <FilterPanel activeCategory={activeCategory} setCategory={(s) => { setCategory(s); setFiltersOpen(false); }} maxPrice={maxPrice} setMaxPrice={setMaxPrice} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <div style={{ width: 220, flexShrink: 0, background: '#fff', borderRadius: 8, padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', alignSelf: 'flex-start', position: 'sticky', top: 76 }}>
            <FilterPanel activeCategory={activeCategory} setCategory={setCategory} maxPrice={maxPrice} setMaxPrice={setMaxPrice} />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search + sort bar */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: isMobile ? '100%' : 200, padding: '9px 14px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14, outline: 'none' }}
            />
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              style={{ padding: '9px 14px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14, background: '#fff', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}
            >
              <option value="">Sort: Newest</option>
              <option value="price">Price: Low to High</option>
              <option value="-price">Price: High to Low</option>
              <option value="created_at">Oldest First</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>Loading products…</div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
              <p style={{ fontSize: 18, marginBottom: 8 }}>No products found.</p>
              <p>Try adjusting your filters.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(230px, 1fr))', gap: isMobile ? 12 : 20 }}>
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>

              {/* Sentinel: IntersectionObserver target */}
              <div ref={sentinelRef} style={{ height: 1, marginTop: 40 }} />

              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#888', fontSize: 14 }}>
                  Loading more…
                </div>
              )}

              {!hasMore && (
                <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>
                  You've seen all {totalCount} product{totalCount !== 1 ? 's' : ''}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
